import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { mayDownloadPhoto, photoDownloadName, type CasePhoto } from '@/domain';
import { useCasePhotoUrls, useDownloadPhoto } from '@/application/usecases/useKits';
import { useObjectUrls } from '@/application/usecases/useObjectUrls';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { downloadBlob, shareFile, sharesFiles } from '@/shared/format/download';
import { strings } from '@/shared/strings';
import { ImageViewer, type ViewerImage } from '@/ui/primitives/ImageViewer';
import { toast } from '@/ui/primitives/toastStore';
import {
  closePhotoViewer,
  usePhotoViewerStore,
  type PhotoViewerRequest,
} from './photoViewerStore';

/**
 * The one image viewer, drawn once in the shell and opened from wherever a
 * photograph is. The request of 5 October 2026 (third), section 6.
 *
 * It closes when the route changes: the photographs belong to the screen they
 * were opened from, and a viewer left over a different case would be showing
 * that case somebody else's pictures. It goes with the shell too, so nothing
 * is left open over the sign in page.
 */
export function PhotoViewerHost() {
  const request = usePhotoViewerStore((state) => state.request);
  const nonce = usePhotoViewerStore((state) => state.nonce);
  const { pathname } = useLocation();

  useEffect(() => closePhotoViewer, [pathname]);

  if (request === null) return null;
  return <OpenViewer key={nonce} request={request} />;
}

/** The photographs on either side of the one on show have their full files fetched ahead. */
const AHEAD = 1;

function OpenViewer({ request }: { request: PhotoViewerRequest }) {
  const { items } = request;
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(request.startIndex, 0), Math.max(items.length - 1, 0)),
  );
  const { data: session } = useSession();
  const download = useDownloadPhoto();

  /*
   * The bytes are fetched here, into object URLs of the viewer's own: a tile
   * revokes its URL when it leaves the screen, and the list under an open
   * viewer refreshes.
   *
   * Two places a photograph: its thumbnail, which the strip it was opened from
   * has already fetched and which is therefore on the screen in the first
   * frame, and its full file, asked for only for the one on show and the one
   * on each side. A phone decodes a twelve megapixel photograph into some
   * fifty megabytes, and a case can have twenty.
   */
  const remote = useCasePhotoUrls(
    items.flatMap((item, at) =>
      item.kind === 'photo'
        ? [item.photo.thumbnailUrl, Math.abs(at - index) <= AHEAD ? item.photo.url : undefined]
        : [undefined, undefined],
    ),
  );
  // A photograph still on the device is its own file, and there is one of it.
  const local = useObjectUrls(items.map((item) => (item.kind === 'local' ? item.file : null)));

  const images: ViewerImage[] = items.map((item, at) => {
    if (item.kind === 'local') {
      const here = local[at] ?? null;
      return {
        id: item.id,
        src: here,
        thumbSrc: here,
        uploadedByName: null,
        uploadedAt: null,
        caption: item.caption,
      };
    }
    const thumb = remote[at * 2];
    const full = remote[at * 2 + 1];
    return {
      id: item.photo.id,
      src: full?.url ?? null,
      thumbSrc: thumb?.url ?? null,
      // A photograph whose uploader has since been removed has no name to print.
      uploadedByName: item.photo.uploadedByUserName === '' ? null : item.photo.uploadedByUserName,
      uploadedAt: item.photo.uploadedAt,
      caption: item.caption,
      failed: full?.failed === true,
    };
  });

  /*
   * Download: for a holder of the permission, and for a photograph the lab
   * has stored. Not offered at all otherwise, so the button never leads to a
   * refusal; the server asks the same question again when it is pressed.
   */
  const current = items[index];
  const stored = current?.kind === 'photo' ? current.photo : null;
  const mayDownload = session !== null && session !== undefined && mayDownloadPhoto(session.permissions);

  function save(photo: CasePhoto) {
    /*
     * The promise and not the mutation's own callbacks: those are dropped if
     * the viewer closes while the file is on its way, and the file would then
     * arrive and go nowhere.
     */
    download.mutateAsync(photo.id).then(
      (blob) => void handOver(blob, photoDownloadName(request.jobOrderNo, photo, blob.type)),
      // The server's own words when it refused.
      (failure: Error) => toast(failureWords(failure, strings.viewer.downloadFailed), { tone: 'error' }),
    );
  }

  return (
    <ImageViewer
      images={images}
      index={index}
      onIndexChange={setIndex}
      origin={request.origin}
      originOf={request.originOf}
      onClose={closePhotoViewer}
      onDownload={mayDownload && stored !== null ? () => save(stored) : undefined}
      downloading={download.isPending}
    />
  );
}

/**
 * Gives a downloaded photograph to the device: through the share sheet on a
 * phone that takes files there, which is where an installed iPhone app saves
 * an image, and as an ordinary download everywhere else.
 *
 * Safari refuses a share that does not come straight from a tap, and by now
 * the tap is a download ago. The file is then offered again on a toast, whose
 * Save is a tap of its own; if that is refused as well it is downloaded
 * plainly, which is better than nothing.
 */
async function handOver(blob: Blob, name: string): Promise<void> {
  const file = new File([blob], name, { type: blob.type });
  if (!sharesFiles(file)) {
    downloadBlob(blob, name);
    return;
  }

  if ((await shareFile(file)) !== 'refused') return;
  toast(strings.viewer.downloadFailed, {
    tone: 'error',
    action: {
      label: strings.viewer.save,
      onClick: () =>
        void shareFile(file).then((outcome) => {
          if (outcome === 'refused') downloadBlob(blob, name);
        }),
    },
  });
}
